import { Injectable } from '@angular/core';
import { CheckoutValidator, ValidationErrorKey, ValidationErrors } from '@checkout/const';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, skipWhile, take, tap } from 'rxjs/operators';
import {
  ApplyingBonusService,
  CheckoutContactFormService,
  PaymentModeService,
  ReceiptService,
  TimeSlotsService,
} from '../services';

export interface ValidatorWithErrors {
  validator: CheckoutValidator;
  errors: ValidationErrors | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class CheckoutValidationService {
  /**
   * Событие с ошибками валидации
   */
  private validationErrorsEvent$ = new BehaviorSubject<ValidationErrors>({});
  /**
   * Событие с ошибками к которым необходимо прокрутить страницу
   */
  private scrollToErrorsEvent$ = new Subject<ValidationErrors>();
  /**
   * При появлении ошибки валидации мы подписываемся на изменения
   * данных от которых зависит ошибка, чтобы когда изменится статус
   * валидации убрать ошибку. Тут хранятся ключи ошибок на которые
   * состоялась подписка.
   */
  private errorsDropQueue = new Set<ValidationErrorKey>();

  constructor(
    private receiptService: ReceiptService,
    private timeSlotsService: TimeSlotsService,
    private paymentModeService: PaymentModeService,
    private checkoutContactFormService: CheckoutContactFormService,
    private applyingBonusService: ApplyingBonusService,
  ) {}

  /**
   * Запуск валидации в чекауте
   * @returns ошибки валидации, если они есть
   */
  validate(): Observable<ValidationErrors | undefined> {
    return combineLatest([
      this.resolveValidation(this.receiptService),
      this.resolveValidation(this.timeSlotsService),
      this.resolveValidation(this.paymentModeService),
      this.resolveValidation(this.checkoutContactFormService),
      this.resolveValidation(this.applyingBonusService),
    ]).pipe(
      tap((result) => {
        let validationErrors: ValidationErrors = {};
        let scrollToErrors: ValidationErrors | undefined;
        const validatorsWithErrors: ValidatorWithErrors[] = [];

        result.forEach((item) => {
          if (!item.errors) {
            return;
          }

          if (!scrollToErrors) {
            scrollToErrors = item.errors;
          }

          validationErrors = { ...validationErrors, ...item.errors };
          validatorsWithErrors.push(item);
        });

        this.validationErrorsEvent$.next(validationErrors);
        this.scrollToErrorsEvent$.next(scrollToErrors);
        this.resetErrorsOnChange(validatorsWithErrors);
      }),
      map((result) => {
        const errors = result.reduce((acc, item) => ({ ...acc, ...item.errors }), {});

        return Object.keys(errors).length ? errors : undefined;
      }),
    );
  }

  /**
   * Подписка на события с ошибками
   * @param errorKeys список ключей ошибок. Если ничего не передать,
   * то будут приходить события со всеми ошибками.
   * @returns события с ошибками
   */
  getErrors(...errorKeys: ValidationErrorKey[]): Observable<ValidationErrors | undefined> {
    return this.validationErrorsEvent$.pipe(
      map((errors) => {
        let filteredErrors = errors;

        if (errorKeys.length) {
          filteredErrors = Object.fromEntries(
            Object.entries(errors).filter((e: [ValidationErrorKey, string]) => errorKeys.includes(e[0])),
          );
        }

        return Object.keys(filteredErrors).length > 0 ? filteredErrors : undefined;
      }),
    );
  }

  /**
   * Подписка на события скролла к ошибке. Если появляется ошибка и
   * к этой ошибке необходимо прокрутить страницу, то будет отправлено
   * событие скролла.
   * @param errorKeys список ключей ошибок.
   * @returns пустые события
   */
  getScrollToEvent(...errorKeys: ValidationErrorKey[]): Observable<void> {
    if (!errorKeys.length) {
      throw new Error('Аргумент errorKeys обязателен!');
    }

    return this.scrollToErrorsEvent$.pipe(
      filter((errors) => Object.keys(errors || {}).some((key: ValidationErrorKey) => errorKeys.includes(key))),
      map(() => undefined),
    );
  }

  /**
   * Сброс сообщений ошибок после смены статуса валидации
   */
  private resetErrorsOnChange(items: ValidatorWithErrors[]): void {
    items.forEach(({ errors, validator }) => {
      Object.keys(errors || {}).forEach((errorKey: ValidationErrorKey) => {
        if (this.errorsDropQueue.has(errorKey)) {
          return;
        }
        this.errorsDropQueue.add(errorKey);
        validator
          .validate()
          .pipe(
            skipWhile((e) => Object.keys(e).length > 0),
            take(1),
          )
          .subscribe(() => {
            const validationErrors = this.validationErrorsEvent$.getValue();
            delete validationErrors[errorKey];
            this.validationErrorsEvent$.next(validationErrors);
            this.errorsDropQueue.delete(errorKey);
          });
      });
    });
  }

  /**
   * Вызывает метод validate в переданном валидаторе
   * @returns статус валидации вместе с валидатором
   */
  private resolveValidation(validator: CheckoutValidator): Observable<ValidatorWithErrors> {
    return validator.validate().pipe(
      take(1),
      map((errors) => ({
        errors: Object.keys(errors).length ? errors : undefined,
        validator,
      })),
    );
  }
}
